import { Component, OnInit } from '@angular/core';
import { FormBase } from '../shared/form-base.component';
import { FormBuilder, FormControl, FormGroup, Validators, AbstractControl, ValidatorFn, ValidationErrors } from '@angular/forms';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { AsmsService } from '../../services/asms.service';
import { ProvisionsService } from '../../services/provisions.service';
import { AzEnvironment, AzManagementGroup, AzTenant, AzureSubscription } from 'src/app/models/azureSubscription.model';
import { AccountStatus } from 'src/app/enums/account-status.enum';
import { AccountType } from 'src/app/enums/account-type.enum';
import { ProvisionEnvironment } from 'src/app/enums/provision-environment.enum';
import { debounceTime, distinctUntilChanged } from 'rxjs';
import { approvedOrgs } from 'src/app/shared/app-data/approved-orgs';


@Component({
  selector: 'app-form-azure',
  templateUrl: './form-azure.component.html',
  styleUrl: '../shared/form.component.scss'
})
export class FormAzureComponent extends FormBase implements OnInit {

  disabled: boolean = true;
  opsOwnerValid: boolean = false;
  checkingOpsOwner: boolean = false;

  firstFormGroup = this._formBuilder.group({
    asms: [null as any, [Validators.required, Validators.minLength(5), Validators.maxLength(5)]],
    environment: ['Dev', Validators.required],
    name: ['', [this.accountNameLengthValidator(), Validators.pattern(/^(?!.*--)[a-zA-Z0-9][a-zA-Z0-9-]*[a-zA-Z0-9]$/)]],
    tenant: [null as AzTenant | null, Validators.required],
    managementGroup: [null as AzManagementGroup | null, Validators.required],
    repos: ['', [Validators.required, this.githubReposValidator()]],
    subBudget: ['', [Validators.required, Validators.pattern(/^\d+(\.\d{1,2})?$/), this.budgetValidator()]],
  });

  secondFormGroup = this._formBuilder.group({
    secondCtrl: ['', Validators.required],

  });
  tenants: AzTenant[] = [];
  managementGroups: AzManagementGroup[] = [];
  envs: AzEnvironment[] = [];
  reposValid: boolean = true;
  tenantName: string = "";

  constructor(
    private _formBuilder: FormBuilder,
    protected override asmsService: AsmsService,
    protected override router: Router,
    public override dialog: MatDialog,
    protected override provisionService: ProvisionsService,

  ) {
    super(router, dialog, provisionService, asmsService);
  }

  ngOnInit(): void {
    this.getAsmsList(this.firstFormGroup);
    this.getEnvs();
    this.getTenants();

    //refresh management groups based on selected tenant
    this.firstFormGroup.controls.tenant.valueChanges
      .subscribe((tenant) => {
        if (tenant) {
          this.getManagementGroups(tenant.id);
        }
    });

    this.firstFormGroup.controls['asms'].valueChanges
    .pipe(debounceTime(300), distinctUntilChanged())
    .subscribe(asms => {
      console.log("Asms: ", asms)
      console.log("Asms length: ", asms.length)
      if (asms.asmsId?.length != 0) {
        this.checkOpsOwner(asms.asmsId);
      } else {
        this.opsOwnerValid = false;
      }
    });

  }

  /**
   * Checks the repo input, if there are more than 10 repos, 
   * or if a value other than a github url is provided, false is 
   * returned, otherwise, true
   * @returns 
   */
  githubReposValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const repos = control.value;
      if (!repos) {
        return null; // No validation errors if the field is empty
      }

      const reposArray = repos.split(',').map((repo: string) => repo.trim());
      if (reposArray.length > 10) {
        return { tooManyRepos: true };
      }

      const githubRepoPattern = /^([a-zA-Z0-9_ -]+)\/([a-zA-Z0-9_-]+)\/?.*$/;
      for (const repo of reposArray) {
        if (!githubRepoPattern.test(repo)) {
          return { invalidRepoFormat: true };
        }

        const match = repo.match(githubRepoPattern);
        //TODO: need to add logic here to check if org name is valid
        //awaiting list of approved orgs
        if (!match) {
          console.log("invalid org format");
          return { invalidRepo: true };
        }

        const orgName = match[1].toUpperCase();
        if (!approvedOrgs.includes(orgName)) {
          console.log("invalid org");
          return { invalidOrg: true }
        }
      }

      return null; // No validation errors
    };
  }

  /**
   * Checks the budget value. Checks if value is a number, 
   * if value is greater than 0
   * @returns null if value passes checks, object with message otherwise 
   */
  budgetValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = parseFloat(control.value);
      return isNaN(value) || value <= 0 ? { 'Error': 'Incorrect value' } : null;
    };
  }

  accountNameLengthValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (this.firstFormGroup && 
        this.firstFormGroup.controls.asms.value && 
        this.firstFormGroup.controls.environment.value)
      {
        const tempAcctName = `${this.firstFormGroup.controls.asms.value.asmsId}-${this.firstFormGroup.controls.environment.value}-${this.firstFormGroup.controls.asms.value.asmsAcronym}${this.firstFormGroup.controls.name.value ? `-${control.value}` : ''}`;
        return (tempAcctName.length <= 40) ? null : { 'accountNameTooLong': true };
      }
      return null;
    };
  }

  /**
   * Gets a list of environments to be displayed in the dropdown
   */
  getEnvs() {
    this.provisionService.getAzEnvs().subscribe({
      next: (res: AzEnvironment[]) => {
        this.envs = res;
      },
      error: (error) => {
        this.accountNameValid = false;
        console.log("Error: unable to get environments");
        console.log(error);
      },
      complete: () => {
        console.log("Request complete");
      }
    });
  }

  /**
   * Gets a list of tenants to be displayed in the dropdown
   */
  getTenants() {
    this.provisionService.getAzTenants().subscribe({
      next: (res: AzEnvironment[]) => {
        this.tenants = res;
      },
      error: (error) => {
        this.accountNameValid = false;
        console.log("Error: unable to get tenants");
        console.log(error);
      },
      complete: () => {
        console.log("Request complete");
      }
    });
  }

  /**
   * Gets a list of management groups to be displayed in the dropdown
   */
  getManagementGroups(tenantId: string) {
    const allowedManagementGroups = ["Online", "Intranet", "Public Isolated", "Software Factory", "Online Atmos"]

    this.provisionService.getAzManagementGroups(tenantId).subscribe({
      next: (res: AzManagementGroup[]) => {
        const filteredManagementGroups = res.filter(group => allowedManagementGroups.includes(group.name));
        this.managementGroups = filteredManagementGroups;
      },
      error: (error) => {
        this.accountNameValid = false;
        console.log("Error: unable to get management groups");
        console.log(error);
      },
      complete: () => {
        console.log("Request complete");
      }
    });
  }

  /**
   * Returns a list of strings of repo names
   * @returns list of strings
   */
  getRepoList() {
    return this.firstFormGroup.controls.repos.value?.split(',').map(item => item.trim());
  }

  /**
   * Gathers all of the input into an object to send to the backend 
   */
  submit() {
    const repos = this.getRepoList();

    const payloadVals = {
      asms_no: this.firstFormGroup.controls.asms.value.asmsId,
      name_suffix: this.firstFormGroup.controls?.name?.value,
      environment: this.firstFormGroup.controls.environment.value as keyof typeof ProvisionEnvironment,
      tenant_id: this.firstFormGroup.controls.tenant.value?.id,
      management_group_id: this.firstFormGroup.controls.managementGroup.value?.id,
      budget: this.firstFormGroup.controls.subBudget.value,
      github_repos: repos,
    }

    const payload = new AzureSubscription(payloadVals);

    this.provisionService.createAzSubscription(payload).subscribe(
      (res: any) => {
      console.log("Payload sent to API");
      console.log(res);
      if (res) {
        const name = `${payloadVals.asms_no}-${payloadVals.environment}-${this.firstFormGroup.controls.asms.value?.asmsAcronym}${payloadVals.name_suffix ? `-${payloadVals.name_suffix}` : ''}`;
        const data = {
          asms: payloadVals.asms_no,
          account_name: name,
          account_status: AccountStatus.Pending,
          type: AccountType.Azure,
          environment: ProvisionEnvironment[payloadVals.environment],
        }
        console.log("start", data);
        this.openAccountSummaryDialog(data);
      }
    },
    (error) => {
      console.log("Error: Unable to create account.");
      this.openAccountSummaryDialog({ error: true });
      return;
    });
  }

  disableSuffixField() {
    if (this.firstFormGroup.controls.environment.value == null || this.firstFormGroup.controls.asms.value == null) {
      this.firstFormGroup.controls.name.disable()
      return true
    } else {
      this.firstFormGroup.controls.name.enable()
      return false
    }
  }

  checkOpsOwner(asms: string) {
    this.checkingOpsOwner = true;
    this.asmsService.checkOperationsOwner(asms).subscribe({
      next: (valid: boolean) => {
        this.opsOwnerValid = valid;
        this.checkingOpsOwner = false;
      },
      error: () => {
        this.opsOwnerValid = false;
        this.checkingOpsOwner = false;
      }
    });
  }

}

